import type { InvoiceScheduleSend } from '@clockwise/clients/invoices';
import { DatePicker } from '@clockwise/ui';
import { useEffect, useState } from 'react';
import { SEND_MODE_LABELS, SEND_MODES, type SendMode } from '@/components/custom/invoice-send-mode';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { todayIso } from '@/lib/user-display';

/**
 * What the dialog says will happen, plainly. Invoices differ across a
 * selection — some drafts, some approved, each with its own invoice date — so
 * this states the rule rather than one invoice's outcome.
 */
export function scheduleSendHint(mode: SendMode, date: string): string {
  if (mode === 'approval') {
    return 'Approved invoices are emailed within about 15 minutes; drafts once they are approved.';
  }
  if (mode === 'manual') return 'Nothing is emailed until someone presses Send.';
  if (date === '') return 'Pick the day to email them.';
  return 'Emailed on this date at the send time set in invoice settings — drafts only once approved. Invoices dated after it are skipped.';
}

/** The body the API takes for a mode and a date, or null while incomplete. */
export function toScheduleSend(mode: SendMode, date: string): InvoiceScheduleSend | null {
  if (mode !== 'date') return { sendMode: mode };
  return date === '' ? null : { sendMode: 'date', scheduledSendOn: date };
}

/**
 * Change when the selected invoices get emailed — the bulk form of the
 * editor's Send field. Asks rather than firing from the bar, because it needs
 * a choice (and usually a date) before there is anything to do.
 */
export function InvoiceScheduleSendDialog({
  open,
  count,
  busy,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  /** How many invoices it would touch: drafts and approved, never sent. */
  count: number;
  busy: boolean;
  onConfirm: (schedule: InvoiceScheduleSend) => void;
  onCancel: () => void;
}) {
  const [mode, setMode] = useState<SendMode>('date');
  const [date, setDate] = useState('');

  // Fresh each time it opens: a date picked for last week's batch is not a
  // sensible default for this one.
  useEffect(() => {
    if (open) {
      setMode('date');
      setDate(todayIso());
    }
  }, [open]);

  const schedule = toScheduleSend(mode, date);
  const noun = count === 1 ? 'invoice' : 'invoices';

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onCancel();
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            Change send date for {count.toLocaleString()} {noun}
          </DialogTitle>
          <DialogDescription>
            Approved invoices stay approved — when to send isn’t part of what was signed off.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-2">
          <Label htmlFor="bulk-send-mode">Send</Label>
          <div className="flex flex-wrap gap-2">
            <Select
              value={mode}
              disabled={busy}
              onValueChange={(v) => {
                const next = v as SendMode;
                setMode(next);
                if (next === 'date' && date === '') setDate(todayIso());
              }}
            >
              <SelectTrigger id="bulk-send-mode" className="w-56" aria-label="Send">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SEND_MODES.map((m) => (
                  <SelectItem key={m} value={m}>
                    {SEND_MODE_LABELS[m]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {mode === 'date' ? (
              <DatePicker
                value={date}
                onChange={setDate}
                disabled={busy}
                placeholder="Pick a date"
                className="w-40"
              />
            ) : null}
          </div>
          <p className="text-muted-foreground text-xs">{scheduleSendHint(mode, date)}</p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button
            disabled={busy || schedule === null}
            onClick={() => schedule && onConfirm(schedule)}
          >
            Update {count.toLocaleString()} {noun}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
